import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { segmentClient } from '@/util/analytics';
import { getPlatformStorage } from '@/util/platformStorage';
import { Sentry } from '@/util/sentry';

const DEBOUNCE_MS = 500;

/**
 * Custom hook for managing chat draft message persistence.
 *
 * Handles GiftedChat's behavior of emitting stale empty events on mount/unmount
 * by only accepting changes that represent real user input.
 * Before first user edit, only accepts non-empty text that differs from persisted
 * value (filters stale events). Exception: allows empty when clearing persisted
 * draft (user select-all-delete). DEBOUNCE_MS (500) balances responsiveness with
 * reduced storage writes. Flushes pending changes on unmount if user made real edits.
 *
 * An optional `initialMessage` (e.g. supplied via a `?message=` URL param) preloads
 * the composer on first load, overriding any saved draft and persisting like a normal
 * draft. Whitespace-only values are ignored. Only the first conversation the hook loads
 * is eligible, so switching grouped tabs cannot clobber a sibling conversation with a
 * stale preload. The value is captured synchronously when the conversation changes so a
 * caller clearing the source param can't race the async load.
 *
 * @param conversationId - The Twilio conversation SID
 * @param userId - The current user's ID for storage scoping
 * @param initialMessage - Optional message to preload into the composer, overriding any saved draft
 */
export function useChatDraft(conversationId: string, userId?: string, initialMessage?: string) {
  const [draftMessage, setDraftMessageState] = useState('');

  const [isLoaded, setIsLoaded] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pendingTextRef = useRef<string | null>(null);

  const persistedValueRef = useRef<string | null>(null);

  const hasUserEditedRef = useRef(false);

  /** Latest initialMessage without retriggering the load effect when it changes. */
  const initialMessageRef = useRef(initialMessage);

  initialMessageRef.current = initialMessage;

  /** Preload is eligible only on the first conversation this hook loads. */
  const hasResolvedFirstLoadRef = useRef(false);

  const storageKey = `${STORAGE_KEYS.CHAT_DRAFT_PREFIX}:${conversationId}`;

  const storage = useMemo(() => getPlatformStorage(userId, 'local'), [userId]);

  /**
   * Loads draft from storage on mount.
   * @remarks conversationId is intentionally not in deps - it's captured via storageKey.
   * When conversationId changes, storageKey changes, triggering this effect.
   */
  useEffect(() => {
    let isMounted = true;

    const preloadMessage = initialMessageRef.current;

    setIsLoaded(false);

    setDraftMessageState('');

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);

      debounceRef.current = null;
    }

    pendingTextRef.current = null;

    hasUserEditedRef.current = false;

    persistedValueRef.current = null;

    const loadDraft = async () => {
      try {
        const savedDraft = await storage.getItem(storageKey);

        if (isMounted) {
          const canPreload =
            !hasResolvedFirstLoadRef.current &&
            !!preloadMessage &&
            preloadMessage.trim().length > 0;

          if (canPreload && preloadMessage) {
            persistedValueRef.current = preloadMessage;

            setDraftMessageState(preloadMessage);

            if (preloadMessage !== savedDraft) {
              storage.setItem(storageKey, preloadMessage).catch((error) => {
                Sentry.captureException(error, {
                  tags: { component: 'useChatDraft', action: 'preload-save' },
                  extra: { conversationId, userId },
                });
              });
            }

            Promise.resolve(
              segmentClient.track(ANALYTICS_EVENTS.INQUIRY_CONVERSATION_DRAFT_PRELOADED, {
                conversationId,
                draftLength: preloadMessage.length,
              })
            ).catch(() => undefined);
          } else if (savedDraft) {
            persistedValueRef.current = savedDraft;

            setDraftMessageState(savedDraft);

            Promise.resolve(
              segmentClient.track(ANALYTICS_EVENTS.INQUIRY_CONVERSATION_DRAFT_RESTORED, {
                conversationId,
                draftLength: savedDraft.length,
              })
            ).catch(() => undefined);
          } else {
            persistedValueRef.current = null;

            setDraftMessageState('');
          }
        }
      } catch (e) {
        console.error('Failed to load chat draft:', e);

        Sentry.captureException(e, {
          tags: { component: 'useChatDraft', action: 'load' },
          extra: { conversationId, userId },
        });
      } finally {
        if (isMounted) {
          hasResolvedFirstLoadRef.current = true;

          setIsLoaded(true);
        }
      }
    };

    Promise.resolve(loadDraft()).catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [storageKey, storage]);

  const setDraftMessage = useCallback(
    (text: string) => {
      const persistedValue = persistedValueRef.current ?? '';

      if (!hasUserEditedRef.current) {
        if (text === '' && persistedValue === '') {
          return;
        }

        if (text === '' && persistedValue !== '') {
          return;
        }

        if (text !== '' && text === persistedValue) {
          return;
        }

        hasUserEditedRef.current = true;
      }

      setDraftMessageState(text);

      pendingTextRef.current = text;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        try {
          if (text) {
            await storage.setItem(storageKey, text);
          } else {
            await storage.removeItem(storageKey);
          }

          persistedValueRef.current = text || null;

          pendingTextRef.current = null;
        } catch (e) {
          console.error('Failed to save chat draft:', e);

          Sentry.captureException(e, {
            tags: { component: 'useChatDraft', action: 'save' },
            extra: { conversationId, userId },
          });
        }
      }, DEBOUNCE_MS);
    },
    [storageKey, storage]
  );

  const clearDraft = useCallback(async () => {
    setDraftMessageState('');

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);

      debounceRef.current = null;
    }

    pendingTextRef.current = null;

    persistedValueRef.current = null;

    hasUserEditedRef.current = false;

    try {
      await storage.removeItem(storageKey);
    } catch (e) {
      console.error('Failed to clear chat draft:', e);

      Sentry.captureException(e, {
        tags: { component: 'useChatDraft', action: 'clear' },
        extra: { conversationId, userId },
      });
    }
  }, [storageKey, storage]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);

        debounceRef.current = null;
      }

      if (pendingTextRef.current !== null && hasUserEditedRef.current) {
        const text = pendingTextRef.current;

        if (text) {
          storage.setItem(storageKey, text).catch((e) => {
            console.error('Failed to save chat draft on unmount:', e);

            Sentry.captureException(e, {
              tags: { component: 'useChatDraft', action: 'unmount-save' },
              extra: { storageKey },
            });
          });
        } else {
          storage.removeItem(storageKey).catch((e) => {
            console.error('Failed to clear chat draft on unmount:', e);

            Sentry.captureException(e, {
              tags: { component: 'useChatDraft', action: 'unmount-clear' },
              extra: { storageKey },
            });
          });
        }

        pendingTextRef.current = null;
      }
    };
  }, [storageKey, storage]);

  return { draftMessage, setDraftMessage, clearDraft, isLoaded };
}
